import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { syncService } from '../services/syncService';
import { QueueOperationType, QueueItem } from '../services/offlineQueue';

interface UseOfflineOperationsReturn {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSynced: number | null;
  queueOperation: (
    operationType: QueueOperationType,
    payload: any,
    idempotencyKey?: string
  ) => Promise<QueueItem>;
  manualSync: () => Promise<void>;
  getSyncStatus: () => Promise<void>;
}

export const useOfflineOperations = (): UseOfflineOperationsReturn => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSynced, setLastSynced] = useState<number | null>(null);

  // Update sync status
  const getSyncStatus = useCallback(async () => {
    try {
      const status = await syncService.getSyncStatus();
      setIsOnline(status.isOnline);
      setIsSyncing(status.isSyncing);
      setPendingCount(status.pendingCount);
      setLastSynced(status.lastSynced);
    } catch (error) {
      console.error('Failed to get sync status:', error);
    }
  }, []);

  // Queue operation
  const queueOperation = useCallback(async (
    operationType: QueueOperationType,
    payload: any,
    idempotencyKey?: string
  ): Promise<QueueItem> => {
    try {
      const item = await syncService.queueOperation(operationType, payload, idempotencyKey);
      
      // Show appropriate message
      if (!isOnline) {
        toast.success('Operation queued for sync when online');
      } else {
        toast.success('Operation queued for background sync');
      }
      
      // Update status
      await getSyncStatus();
      
      return item;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to queue operation';
      toast.error(errorMessage);
      throw error;
    }
  }, [isOnline, getSyncStatus]);

  // Manual sync
  const manualSync = useCallback(async () => {
    if (!isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }

    try {
      setIsSyncing(true);
      await syncService.manualSync();
      toast.success('Sync completed successfully');
      await getSyncStatus();
    } catch (error) {
      toast.error('Sync failed. Please try again.');
      console.error('Manual sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, getSyncStatus]);

  // Setup network listeners and periodic updates
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Connection restored');
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error('Connection lost. Changes will be queued.');
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial status check
    getSyncStatus();

    // Start periodic sync
    syncService.startPeriodicSync(30000); // 30 seconds

    // Periodic status updates
    const statusInterval = setInterval(getSyncStatus, 5000); // 5 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(statusInterval);
      syncService.stopPeriodicSync();
    };
  }, [getSyncStatus]);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    lastSynced,
    queueOperation,
    manualSync,
    getSyncStatus,
  };
}; 